import { useState, useEffect } from 'react';
import type { Position, TrailingStopLossConfig } from '../types/mexc';
import { TrailingStopLossPersistenceService } from '../services/trailingStopLossPersistence';
import { TrailingStopLossEngine } from '../services/trailingStopLossEngine';
import { 
  TrendingUp, 
  TrendingDown, 
  Settings, 
  Info, 
  AlertTriangle, 
  CheckCircle,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';

interface TrailingStopLossConfigProps {
  position: Position;
  onConfigChange?: (config: TrailingStopLossConfig | null) => void;
}

export function TrailingStopLossConfig({ position, onConfigChange }: TrailingStopLossConfigProps) {
  const [config, setConfig] = useState<TrailingStopLossConfig | null>(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [trailingType, setTrailingType] = useState<'percentage' | 'fixed'>('percentage');
  const [trailingDistance, setTrailingDistance] = useState<string>('0.5');
  const [minProfitThreshold, setMinProfitThreshold] = useState<string>('0.1');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const isLong = position.positionType === 1;

  // Load existing configuration
  useEffect(() => {
    const existingConfig = TrailingStopLossPersistenceService.getTSLConfig(position.positionId);
    if (existingConfig) {
      setConfig(existingConfig);
      setIsEnabled(existingConfig.enabled);
      setTrailingType(existingConfig.trailingType);
      setTrailingDistance(existingConfig.trailingDistance.toString());
      setMinProfitThreshold((existingConfig.minProfitThreshold || 0.1).toString());
    }
  }, [position.positionId]);

  const handleToggleEnabled = () => {
    if (isEnabled) {
      // Disabling TSL
      setShowConfirmation(true);
    } else {
      // Enabling TSL
      setIsEnabled(true);
      handleSaveConfig(true);
    }
  };

  const handleSaveConfig = (enabled: boolean = isEnabled) => {
    const newConfig: TrailingStopLossConfig = {
      positionId: position.positionId,
      symbol: position.symbol,
      enabled,
      trailingType,
      trailingDistance: parseFloat(trailingDistance),
      minProfitThreshold: parseFloat(minProfitThreshold),
      isActive: enabled,
      createdAt: config?.createdAt || Date.now(),
      updatedAt: Date.now(),
    };

    // Validate configuration
    if (newConfig.trailingDistance <= 0) {
      alert('Trailing distance must be greater than 0');
      return;
    }

    if (newConfig.minProfitThreshold !== undefined && newConfig.minProfitThreshold < 0) {
      alert('Minimum profit threshold cannot be negative');
      return;
    }

    try {
      TrailingStopLossPersistenceService.saveTSLConfig(newConfig);
      setConfig(newConfig);
      
      // Start TSL engine if not running
      const engine = TrailingStopLossEngine.getInstance();
      if (enabled && !engine.getStatus().isRunning) {
        engine.start();
      }

      onConfigChange?.(newConfig);
      console.log('TSL configuration saved:', newConfig);
    } catch (error) {
      console.error('Failed to save TSL configuration:', error);
      alert('Failed to save trailing stop-loss configuration');
    }
  };

  const handleDisableConfirm = () => {
    setIsEnabled(false);
    setShowConfirmation(false);
    handleSaveConfig(false);
    
    // Deactivate TSL for this position
    const engine = TrailingStopLossEngine.getInstance();
    engine.deactivateTSL(position.positionId, 'Disabled by user');
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(value);
  };

  const calculateExampleStopPrice = () => {
    const currentPrice = position.holdAvgPrice; // Using hold avg price as example
    const distance = parseFloat(trailingDistance);
    
    if (isNaN(distance) || distance <= 0) return null;

    let stopPrice: number;
    if (trailingType === 'percentage') {
      const trailingAmount = currentPrice * (distance / 100);
      stopPrice = isLong ? currentPrice - trailingAmount : currentPrice + trailingAmount;
    } else {
      stopPrice = isLong ? currentPrice - distance : currentPrice + distance;
    }

    return stopPrice;
  };

  if (showConfirmation) {
    return (
      <div className="space-y-4 p-4 bg-warning-50 dark:bg-warning-900/20 rounded-lg border border-warning-200 dark:border-warning-800">
        <div className="flex items-center space-x-3">
          <AlertTriangle className="w-5 h-5 text-warning-600 dark:text-warning-400" />
          <div>
            <h4 className="font-medium text-warning-800 dark:text-warning-200">
              Disable Trailing Stop-Loss?
            </h4>
            <p className="text-sm text-warning-700 dark:text-warning-300">
              This will stop automatically adjusting your stop-loss price. Your current stop-loss order will remain active.
            </p>
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={() => setShowConfirmation(false)}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDisableConfirm}
            className="flex-1 px-4 py-2 bg-warning-600 hover:bg-warning-700 text-white rounded-lg transition-colors"
          >
            Disable TSL
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* TSL Status Header */}
      <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-center space-x-3">
          {isLong ? (
            <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          ) : (
            <TrendingDown className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          )}
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-100">
              Trailing Stop-Loss
            </h4>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {isLong ? 'Long Position' : 'Short Position'} • {position.symbol}
            </p>
          </div>
        </div>
        
        <button
          onClick={handleToggleEnabled}
          className="flex items-center space-x-2 text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 transition-colors"
        >
          {isEnabled ? (
            <ToggleRight className="w-6 h-6 text-green-600" />
          ) : (
            <ToggleLeft className="w-6 h-6 text-gray-400" />
          )}
          <span className="text-sm font-medium">
            {isEnabled ? 'Enabled' : 'Disabled'}
          </span>
        </button>
      </div>

      {/* Configuration Panel */}
      {isEnabled && (
        <div className="space-y-4">
          {/* Trailing Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Trailing Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setTrailingType('percentage')}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                  trailingType === 'percentage'
                    ? 'bg-primary-100 dark:bg-primary-900/30 border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300'
                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                Percentage
              </button>
              <button
                onClick={() => setTrailingType('fixed')}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                  trailingType === 'fixed'
                    ? 'bg-primary-100 dark:bg-primary-900/30 border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300'
                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                Fixed Amount
              </button>
            </div>
          </div>

          {/* Trailing Distance */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Trailing Distance {trailingType === 'percentage' ? '(%)' : '($)'}
            </label>
            <input
              type="number"
              step={trailingType === 'percentage' ? '0.1' : '0.01'}
              min="0"
              value={trailingDistance}
              onChange={(e) => setTrailingDistance(e.target.value)}
              placeholder={trailingType === 'percentage' ? '0.5' : '1.00'}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {trailingType === 'percentage' 
                ? 'Percentage distance from highest/lowest price'
                : 'Fixed dollar amount distance from highest/lowest price'
              }
            </p>
          </div>

          {/* Advanced Settings Toggle */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center space-x-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
          >
            <Settings className="w-4 h-4" />
            <span>Advanced Settings</span>
          </button>

          {/* Advanced Settings */}
          {showAdvanced && (
            <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Minimum Profit Threshold (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={minProfitThreshold}
                  onChange={(e) => setMinProfitThreshold(e.target.value)}
                  placeholder="0.1"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Minimum profit percentage before trailing begins
                </p>
              </div>
            </div>
          )}

          {/* Example Calculation */}
          {trailingDistance && !isNaN(parseFloat(trailingDistance)) && (
            <div className="p-3 bg-info-50 dark:bg-info-900/20 rounded-lg border border-info-200 dark:border-info-800">
              <div className="flex items-start space-x-2">
                <Info className="w-4 h-4 text-info-600 dark:text-info-400 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-info-800 dark:text-info-200 mb-1">
                    Example Calculation
                  </p>
                  <p className="text-info-700 dark:text-info-300">
                    Current Price: {formatCurrency(position.holdAvgPrice)}
                  </p>
                  {calculateExampleStopPrice() && (
                    <p className="text-info-700 dark:text-info-300">
                      Initial Stop Price: {formatCurrency(calculateExampleStopPrice()!)}
                    </p>
                  )}
                  <p className="text-xs text-info-600 dark:text-info-400 mt-1">
                    Stop-loss will automatically adjust as price moves in your favor
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <button
            onClick={() => handleSaveConfig()}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            <CheckCircle className="w-4 h-4" />
            <span>Save Configuration</span>
          </button>
        </div>
      )}

      {/* Current Status Display */}
      {config && config.enabled && (
        <div className="p-4 bg-success-50 dark:bg-success-900/20 rounded-lg border border-success-200 dark:border-success-800">
          <div className="flex items-center space-x-2 mb-2">
            <CheckCircle className="w-4 h-4 text-success-600 dark:text-success-400" />
            <span className="text-sm font-medium text-success-800 dark:text-success-200">
              TSL Active
            </span>
          </div>
          <div className="text-xs text-success-700 dark:text-success-300 space-y-1">
            <p>Distance: {config.trailingDistance}{config.trailingType === 'percentage' ? '%' : '$'}</p>
            {config.currentTrailingStopPrice && (
              <p>Current Stop: {formatCurrency(config.currentTrailingStopPrice)}</p>
            )}
            {config.lastUpdateTime && (
              <p>Last Updated: {new Date(config.lastUpdateTime).toLocaleTimeString()}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
